import { Injectable, inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, finalize, shareReplay, switchMap } from 'rxjs';
import { AUTH_CONFIG, AuthToken } from './auth.config';
import { AuthService } from './auth.service';
import { LOGOUT_REASON_EXPIRED } from './error.interceptor';
import { StorageService } from './storage.service';
import { TokenStore } from './token.store';

/**
 * One refresh at a time, and **everything waits for it**.
 *
 * This was a boolean, and a request that arrived while a refresh was running was sent straight
 * through on the strength of it — with no token, because the reason a refresh was running was
 * that there wasn't one. A screen that opens two calls together (juno's `/salons` reads its
 * facets and its list in the same tick) therefore always 401'd on the second.
 *
 * Holding the in-flight refresh instead means every caller subscribes to the same one and every
 * caller waits. `shareReplay` so a subscriber arriving after it resolves still gets the answer;
 * `finalize` clears it so the next expiry starts a new one.
 */
@Injectable({ providedIn: 'root' })
export class RefreshGate {
  private inFlight: Observable<unknown> | null = null;

  /** true while a refresh is running, for callers that only need to know */
  get inProgress(): boolean {
    return this.inFlight !== null;
  }

  run(refresh: () => Observable<unknown>): Observable<unknown> {
    this.inFlight ??= refresh().pipe(
      finalize(() => (this.inFlight = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.inFlight;
  }
}

/**
 * Keeps the access token fresh before a request goes out:
 *  - no refresh token at all: the session is gone, force logout
 *  - access token missing or about to expire: refresh first, then send
 * Runs first in the chain so tokenCachingInterceptor mints the header from the new token.
 */
export const tokenRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(AUTH_CONFIG);
  const router = inject(Router);
  const storage = inject(StorageService);
  const auth = inject(AuthService);
  const tokens = inject(TokenStore);
  const gate = inject(RefreshGate);

  if (req.url.includes(config.signInPath) || req.url.includes(config.refreshPath)) {
    return next(req);
  }

  const forceLogout = (): void => {
    // the refresh token is gone: the session is over, and the login page says so (?reason=expired)
    auth.logOut();
    router.navigate([config.loginPath], { queryParams: { reason: LOGOUT_REASON_EXPIRED } }).then(() => {
      if (config.reloadOnLogout) window.location.reload();
    });
  };

  if (tokens.refreshToken() === null) {
    forceLogout();
    return next(req);
  }

  // only API calls carry the token, so only they wait for a refresh (assets, third parties go straight out)
  if (!req.url.startsWith(config.baseUrl)) return next(req);

  const expiresSoon = (accessToken: AuthToken): boolean =>
    accessToken.expiresAt - Date.now() <= config.refreshTokenExpMinutes * 60 * 1000;

  const accessToken = tokens.accessToken();
  const isLoggedIn = storage.getUser() != null && accessToken != null;

  // a token that is here and good for a while: nothing to wait for
  if (isLoggedIn && !expiresSoon(accessToken)) return next(req);

  /*
   * Otherwise wait. Two cases reach here and both must hold the request rather than send it:
   *  - no access token at all: a reload, since it lives in memory only (TokenStore)
   *  - one that expires within the window: refresh before spending it
   * Every request that arrives meanwhile joins the SAME refresh and is sent after it, so a
   * screen that opens several calls at once gets a token on all of them, not on the first.
   */
  return gate.run(() => auth.refreshToken()).pipe(switchMap(() => next(req)));
};
